import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicShell } from "@/components/PublicNav";
import { SEO } from "@/components/SEO";
import { CheckCircle2, Info, X, ArrowRight, Lock } from "lucide-react";
import { api, formatErr, inr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { openCheckout } from "@/lib/razorpay";

const PLANS = {
  monthly: { fee: 1299, label: "Monthly", per: "/month", renew: "₹1,299/month" },
  quarterly: { fee: 3000, label: "Quarterly", per: "/3 months", renew: "₹3,000/quarter", best: true },
};
const SETUP = 2999;

const PlanCard = ({ planKey, onChoose }) => {
  const plan = PLANS[planKey];
  return (
    <div className="zq-card p-7 relative" data-testid={`plan-${planKey}`}>
      {plan.best && (
        <span className="zq-pill zq-pill-orange absolute -top-3 left-7">Best Value</span>
      )}
      <h3 className="text-xl font-bold">{plan.label}</h3>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-4xl font-bold">{inr(plan.fee)}</span>
        <span className="text-zq-muted">{plan.per}</span>
      </div>
      <p className="text-xs text-zq-muted mt-1">Renewal: {plan.renew} (no setup fee again)</p>
      <ul className="mt-5 space-y-2 text-sm">
        {[
          "Unlimited menu items",
          "Multilanguage (EN/HI/GU)",
          "WhatsApp ordering",
          "Loyalty badges",
          "Analytics + Feedback CRM",
          "Account manager",
        ].map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-zq-orange mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <button
        className="zq-btn zq-btn-primary w-full mt-6"
        onClick={() => onChoose(planKey)}
        data-testid={`choose-${planKey}`}
      >
        Get started
      </button>
    </div>
  );
};

export const Pricing = () => {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [open, setOpen] = useState(null); // selected plan key
  const [step, setStep] = useState("review"); // review | success | signup
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [signup, setSignup] = useState({
    name: "", whatsapp_number: "", address: "", contact_number: "",
    google_review_link: "", login_id: "", password: "",
  });

  const choose = (k) => { setOpen(k); setStep("review"); setPayment(null); };

  const close = () => { setOpen(null); setPayment(null); setStep("review"); };

  const pay = async () => {
    setLoading(true);
    try {
      // 1) Backend creates a Razorpay Order
      const { data: order } = await api.post("/public/payments/create-order", { plan: open });
      // 2) Open Razorpay Checkout
      const resp = await openCheckout({
        key_id: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "ZaykaQR",
        description: `${open === "monthly" ? "Monthly" : "Quarterly"} plan + Setup`,
      });
      // 3) Verify signature on backend
      const { data: verified } = await api.post("/public/payments/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        plan: open,
      });
      setPayment(verified.payment);
      setStep("success");
      toast.success("Payment successful! Now create your account.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  const startSignup = () => setStep("signup");

  const completeSignup = async (e) => {
    e.preventDefault();
    if (!payment?.id) return;
    setLoading(true);
    try {
      const { data } = await api.post("/public/signup", { ...signup, payment_id: payment.id });
      localStorage.setItem("zq_token", data.token);
      await refresh();
      toast.success(`Welcome, ${data.user.name}! Your dashboard is ready.`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const planFee = open ? PLANS[open].fee : 0;
  const total = planFee + SETUP;

  return (
    <PublicShell>
      <SEO
        title="Pricing — ZaykaQR"
        description="Simple INR pricing for Indian restaurants. ₹1,299/month or ₹3,000/quarter + one-time ₹2,999 setup. Zero commission, ever."
        url="https://zaykaqr.space/pricing"
      />
      <section className="zq-hero-bg">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-20 text-center">
          <span className="zq-pill zq-pill-orange">Pricing</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">Simple plans. No commission. Ever.</h1>
          <p className="text-zq-muted mt-5 max-w-2xl mx-auto">All plans in ₹. Pay once, run forever.</p>
        </div>
      </section>

      <section className="zq-section pt-0">
        <div className="max-w-5xl mx-auto">
          <div className="zq-card p-4 flex items-start gap-3 border-l-4 border-zq-orange mb-8" data-testid="setup-fee-banner">
            <Info className="text-zq-orange shrink-0" size={20} />
            <div>
              <p className="font-semibold">One-time Setup Fee: ₹2,999</p>
              <p className="text-sm text-zq-muted">Automatically added to your first payment. Renewals do not include setup fee.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <PlanCard planKey="monthly" onChoose={choose} />
            <PlanCard planKey="quarterly" onChoose={choose} />
          </div>

          <p className="text-xs text-zq-muted text-center mt-8 flex items-center justify-center gap-1.5">
            <Lock size={12}/> Secure payment via <span className="font-semibold">Razorpay</span> — your card details never touch our servers.
          </p>
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="checkout-modal">
          <div className="zq-card w-full max-w-md p-6 bg-zq-cream relative my-8">
            <button className="absolute top-3 right-3 text-zq-muted" onClick={close} data-testid="checkout-close">
              <X size={18} />
            </button>

            {step === "review" && (
              <>
                <h3 className="text-xl font-bold">Confirm payment</h3>
                <p className="text-sm text-zq-muted mt-1">Plan: <span className="font-semibold capitalize">{open}</span></p>
                <div className="mt-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span>Setup Fee</span><span>{inr(SETUP)}</span></div>
                  <div className="flex justify-between"><span>{open === "monthly" ? "First Month" : "First Quarter"}</span><span>{inr(planFee)}</span></div>
                  <div className="border-t border-zq-border my-2"></div>
                  <div className="flex justify-between font-bold text-base"><span>Total Today</span><span>{inr(total)}</span></div>
                </div>
                <button className="zq-btn zq-btn-primary w-full mt-6" onClick={pay} disabled={loading} data-testid="checkout-pay">
                  {loading ? "Opening Razorpay…" : `Pay ${inr(total)} securely`}
                </button>
                <p className="text-xs text-zq-muted text-center mt-3 flex items-center justify-center gap-1"><Lock size={10}/> Powered by Razorpay · Cards · UPI · Netbanking · Wallets</p>
              </>
            )}

            {step === "success" && (
              <>
                <CheckCircle2 className="text-zq-orange mx-auto" size={42} />
                <h3 className="text-xl font-bold text-center mt-3">Payment successful</h3>
                <p className="text-sm text-zq-muted text-center mt-1">
                  Order ID: <span className="font-mono text-xs">{payment?.razorpay_order_id}</span>
                </p>
                <p className="text-sm text-center mt-4">Now let's create your restaurant account so you can start using ZaykaQR right away.</p>
                <button className="zq-btn zq-btn-primary w-full mt-5" onClick={startSignup} data-testid="start-signup">
                  Create my account <ArrowRight size={16}/>
                </button>
              </>
            )}

            {step === "signup" && (
              <form onSubmit={completeSignup} data-testid="signup-form">
                <h3 className="text-xl font-bold">Create your account</h3>
                <p className="text-xs text-zq-muted mt-1">Just a few details to set up your dashboard.</p>
                <div className="mt-4 space-y-3">
                  <input className="zq-input" placeholder="Restaurant name *" required value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} data-testid="signup-name"/>
                  <input className="zq-input" placeholder="WhatsApp number * (e.g. +919999999999)" required value={signup.whatsapp_number} onChange={(e) => setSignup({ ...signup, whatsapp_number: e.target.value })} data-testid="signup-whatsapp"/>
                  <input className="zq-input" placeholder="Address" value={signup.address} onChange={(e) => setSignup({ ...signup, address: e.target.value })} data-testid="signup-address"/>
                  <input className="zq-input" placeholder="Contact number" value={signup.contact_number} onChange={(e) => setSignup({ ...signup, contact_number: e.target.value })}/>
                  <input className="zq-input" placeholder="Google review link" value={signup.google_review_link} onChange={(e) => setSignup({ ...signup, google_review_link: e.target.value })}/>
                  <div className="border-t border-zq-border my-2"></div>
                  <input className="zq-input" placeholder="Login ID *" required value={signup.login_id} onChange={(e) => setSignup({ ...signup, login_id: e.target.value })} data-testid="signup-login-id"/>
                  <input className="zq-input" type="password" placeholder="Password *" required minLength={6} value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} data-testid="signup-password"/>
                </div>
                <button className="zq-btn zq-btn-primary w-full mt-5" disabled={loading} data-testid="signup-submit">
                  {loading ? "Creating…" : "Create account & open dashboard"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </PublicShell>
  );
};
